import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Autocomplete from "src/app/components/autocomplete/Autocomplete";
import { Icon } from "src/app/components/icon/Icon";
import Calculator from "src/app/modules/calculator/Calculator";
import { ROOM_DATA, ROOM_TYPES, SYSTEM_DATA } from "src/app/modules/home/constants";

import homeCSS from "./Home.module.css";
import Help from "./components/Help";

const Home = ({ showAuthModal, showPartnerModal }: { showAuthModal: (formType: string) => void; showPartnerModal: (isOpen: boolean) => void }) => {
  const [focusInput, setFocusInput] = useState(false);
  const [imageSource, setImageSource] = useState(ROOM_DATA.find((x) => x.name === ROOM_TYPES.INTRO)?.imageSource ?? "");
  const [videoSource, setVideoSource] = useState("");
  const [videoActive, setVideoActive] = useState(false);
  const [isDesktopMedia, setIsDesktopMedia] = useState(true);
  const [systemGroupSelectActive, setSystemGroupSelectActive] = useState(true);
  const [roomSelectActive, setRoomSelectActive] = useState(false);
  const [systemSelectActive, setSystemSelectActive] = useState(false);

  const [systemGroup, setSystemGroup] = useState<any | undefined>({});
  const [room, setRoom] = useState<any | undefined>({});
  const [system, setSystem] = useState<any | undefined>({});

  const [rooms, setRooms] = useState<Array<any> | undefined>([]);
  const [systems, setSystems] = useState<Array<any> | undefined>([]);
  const [unassignedSystems, setUnAssignedSystems] = useState<Array<any>>([]);

  const [activeSelect, setActiveSelect] = useState("");
  const [helpVideoModalActive, setHelpVideoModalActive] = useState(false);

  let history = useHistory();

  const location = useLocation();
  const search = location.search;
  const params = new URLSearchParams(search);
  const projectId = params.get("upitBroj");

  useEffect(() => {
    const windowResized = (event: MediaQueryList) => {
      if (event.matches) {
        setIsDesktopMedia(false);
      } else {
        setIsDesktopMedia(true);
      }
    };
    const mediaQuery = "(max-width: 800px)";
    const mediaQueryList = window.matchMedia(mediaQuery);
    windowResized(mediaQueryList);
    mediaQueryList.addEventListener("change", () => windowResized(mediaQueryList));
    return () => {
      document.removeEventListener("change", () => windowResized(mediaQueryList));
    };
  });

  const toggleHelpVideoModal = () => {
    setHelpVideoModalActive(!helpVideoModalActive);
  };

  function removeProjectId() {
    if (params.has("upitBroj")) {
      params.delete("upitBroj");
      history.replace({
        search: params.toString(),
      });
    }
  }

  const applyImageSource = () => {
    setTimeout(function () {
      if (roomSelectActive && !systemGroupSelectActive) {
        let targetRoom = ROOM_DATA.find((x) => x.name === ROOM_TYPES.STARTING);
        setImageSource(targetRoom?.imageSource ?? "");
      } else if (!roomSelectActive && systemGroupSelectActive) {
        let targetRoom = ROOM_DATA.find((x) => x.name === ROOM_TYPES.INTRO);
        setImageSource(targetRoom?.imageSource ?? "");
      } else {
        setImageSource(room?.imageSource ?? "");
      }
    }, 750);
  };

  function selectSystemGroup(systemTypeId: number) {
    let targetRoom = ROOM_DATA.find((x) => x.name === ROOM_TYPES.STARTING);
    let selectedSystemGroup = SYSTEM_DATA.find((x) => x.typeId === systemTypeId);

    setVideoSource(targetRoom?.videoSource ?? "");
    setSystemGroup(selectedSystemGroup);
    setRooms(selectedSystemGroup?.rooms);
    setSystem({});
    setVideoActive(true);
    setSystemGroupSelectActive(false);
    setRoomSelectActive(true);
  }

  function selectRoom(targetRoom: any, setImageOnly = false, selectedSystemGroup?: any | null) {
    if (!setImageOnly) {
      setVideoSource(targetRoom?.videoSource ?? "");
      setVideoActive(true);
      setRoomSelectActive(false);
      setSystemGroupSelectActive(false);
      setSystemSelectActive(true);
      setRoom(targetRoom);
      setSystem({});
      setSystems(targetRoom?.systems);
      if (selectedSystemGroup) {
        setUnAssignedSystems(selectedSystemGroup?.unassignedSystems);
      } else {
        setUnAssignedSystems(systemGroup?.unassignedSystems);
      }
    } else {
      setActiveSelect(targetRoom.name);
    }
  }

  function selectSystem(subSystemName: string, currentRoom?: any | null) {
    let selectedRoom = ROOM_DATA.find((x) => x.name === room.name);
    if (currentRoom) {
      selectedRoom = currentRoom;
    }
    let system = selectedRoom?.systems.find((x) => x.name === subSystemName);
    setActiveSelect(subSystemName);
    if (system?.imageSource) {
      setImageSource(system?.imageSource ?? "");
    }
    setSystem({});
  }

  function unselectSystem(targetRoom: any) {
    setImageSource(targetRoom?.imageSource ?? "");
    setActiveSelect("");
  }

  function clearSystemAdded() {
    setSystem({});
  }

  const goLevelBack = () => {
    let targetRoom = ROOM_DATA.find((x) => x.name === ROOM_TYPES.INTRO);
    let currentRoom = ROOM_DATA.find((x) => x.name === ROOM_TYPES.STARTING);
    if (systemSelectActive) {
      currentRoom = ROOM_DATA.find((x) => x.name === room.name);
      targetRoom = ROOM_DATA.find((x) => x.name === ROOM_TYPES.STARTING);
    }
    setVideoSource(currentRoom?.reverseVideoSource ?? "");
    setVideoActive(true);

    setSystemGroupSelectActive(roomSelectActive);
    setRoomSelectActive(!roomSelectActive);
    setSystemSelectActive(false);

    setRoom(roomSelectActive ? {} : room);
    setSystems([]);
    setTimeout(function () {
      setActiveSelect(targetRoom?.name ?? "");
    }, 500);
  };

  const addSystem = (system: any, shouldAnimate?: boolean) => {
    if (shouldAnimate) {
      let systemGroup = SYSTEM_DATA.find((x) => x.typeId === system.sysGroupId);
      let targetRoom = SYSTEM_DATA.find((x) => x.typeId === system.sysGroupId)?.rooms.find((x) => x.systems.filter((z) => z.id === system.id).length > 0);
      if (targetRoom) {
        setSystemGroup(systemGroup);
        setRooms(systemGroup?.rooms);
        setTimeout(function () {
          selectRoom(targetRoom, false, systemGroup);
          setTimeout(function () {
            selectSystem(system.name, targetRoom);
            setTimeout(function () {
              setSystem(system);
            }, 1000);
          }, 1000);
        }, 1000);
      } else {
        setSystem(system);
      }
    } else {
      setSystem(system);
    }
  };

  function scrollToSystemSearch() {
    setFocusInput(true);
    setTimeout(() => {
      document.getElementById("introImage")?.scrollIntoView({ behavior: "smooth" });
      setFocusInput(false);
    }, 250);
  }

  return (
    <div className={classNames("u-bg-background", homeCSS.container)}>
      <div className={classNames("u-bg-white", "u-flex", homeCSS.stepContainer)}>
        <div onClick={() => scrollToSystemSearch()} className={classNames("u-flex", "u-flex-items-center", "u-cursor-pointer", homeCSS.stepItem)}>
          <Icon name="first-step" className={homeCSS.stepLogo} />
          <Icon name="first-step-mob" className={homeCSS.stepLogoMob} />
          <h4 className="u-t-secondary-dark">Izaberite sustav koji vam je potreban</h4>
        </div>
        <div className={classNames("u-flex", "u-flex-items-center", homeCSS.stepItem)}>
          <Icon name="second-step" className={homeCSS.stepLogo} />
          <Icon name="second-step-mob" className={homeCSS.stepLogoMob} />
          <h4 className="u-t-primary-dark">Unesite potrebnu kvadraturu</h4>
        </div>
        <div className={classNames("u-flex", "u-flex-items-center", homeCSS.stepItem)}>
          <Icon name="third-step" className={homeCSS.stepLogo} />
          <Icon name="third-step-mob" className={homeCSS.stepLogoMob} />
          <h4 className="u-t-primary">Registrirajte se i pošaljite upit našim partnerima</h4>
        </div>
      </div>
      <div id="introImage" className={classNames("u-flex", "u-flex-content-center", "u-flex-items-center", homeCSS.introImgContainer)}>
        <div className={classNames(homeCSS.introWrapMobile)}>
          <div className="u-flex u-flex-column u-flex-items-center">
            <Autocomplete addSystem={addSystem} focusInput={focusInput} />
            {!isDesktopMedia && <Help helpVideoModalActive={helpVideoModalActive} toggleHelpVideoModal={toggleHelpVideoModal} />}
          </div>
          <img className={homeCSS.imageCSS} alt="slika sobe" src={ROOM_DATA.find((x) => x.name === ROOM_TYPES.INTRO)?.imageSource ?? ""}></img>
        </div>
        <div className={classNames(homeCSS.introWrap)}>
          <Autocomplete addSystem={addSystem} focusInput={false} />
          {isDesktopMedia && <Help helpVideoModalActive={helpVideoModalActive} toggleHelpVideoModal={toggleHelpVideoModal} />}
          {!systemGroupSelectActive && <BackButton goBack={goLevelBack} />}
          <RoomVideo
            classes={videoActive ? "" : "u-none"}
            videoSrc={videoSource}
            videoActive={videoActive}
            closeVideo={setVideoActive}
            applyImageSource={applyImageSource}
          />
          <RoomImage classes={""} imageSrc={imageSource} />
          {systemGroupSelectActive &&
            !videoActive &&
            SYSTEM_DATA.map((system) => <SystemGroupSelect key={system.name} system={system} selectSystemGroup={selectSystemGroup} />)}
          {roomSelectActive &&
            !videoActive &&
            rooms &&
            rooms.map((room, index) => <RoomSelect key={index} systemGroup={systemGroup} room={room} selectRoom={selectRoom} />)}
          {systemSelectActive &&
            systems &&
            systems.map((system) => (
              <SubSystemSelect
                isAssignedSubSystem={true}
                systemGroup={systemGroup}
                room={room}
                system={system}
                unassignedSubSystems={null}
                isActiveSelect={activeSelect === system.name}
                selectSystem={selectSystem}
                selectRoom={selectRoom}
                addSystem={addSystem}
                unselectSystem={unselectSystem}
              />
            ))}
          {systemSelectActive && unassignedSystems && (
            <SubSystemSelect
              isAssignedSubSystem={false}
              systemGroup={systemGroup}
              room={room}
              system={null}
              unassignedSubSystems={unassignedSystems}
              isActiveSelect={activeSelect === room.name}
              selectSystem={selectSystem}
              selectRoom={selectRoom}
              addSystem={addSystem}
              unselectSystem={unselectSystem}
            />
          )}
        </div>
      </div>
      <Calculator
        systemId={system.id}
        projectId={projectId}
        clearSystemAdded={clearSystemAdded}
        showAuthModal={(formType: string) => showAuthModal(formType)}
        showPartnerModal={(isOpen: boolean) => showPartnerModal(isOpen)}
        removeProjectId={() => removeProjectId()}
      />
    </div>
  );
};
export default Home;

const BackButton = ({ goBack }: { goBack: () => void }) => {
  return (
    <button className={classNames("u-pos-absolute", "u-cursor-pointer", homeCSS.circle, homeCSS.blue, homeCSS.backBtnContainer)} onClick={() => goBack()}>
      <span className={classNames(homeCSS.iconContainer, homeCSS.addIcon)}>
        <Icon name="back-arrow" className="u-t-valign-mid u-t-f18p u-t-w700" />
      </span>
      <span className={classNames("u-pos-absolute", "u-t-w700", "u-wauto", "u-t-uppercase", homeCSS.backBtn)}>Nazad</span>
    </button>
  );
};

const SystemGroupSelect = ({ system, selectSystemGroup }: { system: any; selectSystemGroup: (systemTypeId: number) => void }) => {
  return (
    <button
      className={classNames("u-pos-absolute", "u-cursor-pointer", homeCSS.selectBtn, homeCSS.circle, homeCSS.pulse, homeCSS.blue, system.class)}
      onClick={() => selectSystemGroup(system.typeId)}
    >
      <span className={classNames(homeCSS.iconContainer, homeCSS.addIcon)}>
        <Icon name="plus" className="u-t-valign-mid u-t-f12p" />
      </span>
      <span className={classNames("u-pos-absolute", "u-t-w700", "u-wauto", "u-t-uppercase", homeCSS.tooltip, homeCSS.selectTooltip)}>{system.name}</span>
    </button>
  );
};

const RoomSelect = ({ systemGroup, room, selectRoom }: { systemGroup: any; room: any; selectRoom: (roomType: any) => void }) => {
  let systemInRoom = room.systems.map((system: any) => system.group).join(", ");
  return (
    <button
      className={classNames("u-pos-absolute", "u-cursor-pointer", homeCSS.selectBtn, homeCSS.circle, homeCSS.pulse, homeCSS.blue, room.class)}
      onClick={() => selectRoom(room)}
    >
      <span className={classNames(homeCSS.iconContainer, homeCSS.addIcon)}>
        <Icon name="plus" className="u-t-valign-mid u-t-f12p" />
      </span>
      <span className={classNames("u-pos-absolute", "u-t-w700", "u-wauto", "u-t-uppercase", homeCSS.tooltip, homeCSS.selectTooltip)}>
        {systemGroup.name}: {systemInRoom}
      </span>
    </button>
  );
};

const SubSystemSelect = ({
  isAssignedSubSystem,
  systemGroup,
  room,
  system,
  unassignedSubSystems,
  isActiveSelect,
  selectSystem,
  selectRoom,
  addSystem,
  unselectSystem,
}: {
  isAssignedSubSystem: boolean;
  systemGroup: any;
  room: any;
  system: any | null;
  unassignedSubSystems: Array<any> | null;
  isActiveSelect: boolean;
  selectSystem: (system: string) => void;
  selectRoom: (room: string, changeImageOnly: boolean) => void;
  addSystem: (system: any) => void;
  unselectSystem: (room: string) => void;
}) => {
  return (
    <div
      className={classNames(
        "u-cursor-pointer",
        homeCSS.subSystemContainer,
        isAssignedSubSystem ? system.class : systemGroup.unassignedSubSysClass,
        isActiveSelect ? homeCSS.activeSelect : null
      )}
    >
      <button
        className={classNames("u-pos-absolute", "u-cursor-pointer", homeCSS.circle, homeCSS.pulse, homeCSS.blue)}
        onClick={() => (isActiveSelect ? {} : isAssignedSubSystem ? selectSystem(system.name) : selectRoom(room, true))}
      >
        <span onClick={() => (isActiveSelect ? unselectSystem(room) : {})} className={classNames("u-cursor-pointer", homeCSS.iconContainer, homeCSS.addIcon)}>
          <Icon name="plus" className={classNames("u-t-valign-mid", "u-t-f16p", homeCSS.icon)} />
        </span>
        <span className={classNames("u-pos-absolute", "u-t-w700", "u-wauto", "u-t-uppercase", homeCSS.tooltip, homeCSS.selectTooltip)}>
          {isAssignedSubSystem && `${systemGroup.name}: ${system.group}`}
          {!isAssignedSubSystem && `Ostali ${systemGroup.name}`}
        </span>
      </button>
      <ul className={classNames("u-p0", homeCSS.subSystemsTooltip)}>
        {isAssignedSubSystem && <SubSystemItem system={system} addSystem={addSystem} />}
        {!isAssignedSubSystem &&
          unassignedSubSystems &&
          unassignedSubSystems.map((system, index) => <SubSystemItem key={index} system={system} addSystem={addSystem} />)}
      </ul>
    </div>
  );
};

const SubSystemItem = ({ system, addSystem }: { system: any; addSystem: (system: any) => void }) => {
  return (
    <li
      onClick={() => addSystem(system)}
      className={classNames("u-flex", "u-flex-content-start", "u-flex-items-center", "u-cursor-pointer", homeCSS.subSystemsTooltipItem)}
    >
      <button className={classNames("u-cursor-pointer", homeCSS.circle)}>
        <span className={homeCSS.addIcon}>
          <Icon name="plus" className="u-t-valign-mid u-t-f16p" />
        </span>
      </button>
      <span className={classNames("u-t-w700", "u-wauto", "u-t-uppercase", "u-ml-20px", homeCSS.tooltip)}>{system.name}</span>
    </li>
  );
};

const RoomVideo = ({
  videoSrc,
  videoActive,
  classes,
  closeVideo,
  applyImageSource,
}: {
  videoSrc: string;
  videoActive: boolean;
  classes: string;
  closeVideo: (active: boolean) => void;
  applyImageSource: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoActive && videoRef && videoRef.current) {
      videoRef.current.src = videoSrc;
      playVideo();
    }
  }, [videoActive, videoSrc]);

  function playVideo() {
    videoRef.current?.play();
  }

  function pauseVideo() {
    if (videoRef && videoRef.current && videoRef.current.currentTime > videoRef.current.duration - 0.1) {
      closeVideo(false);
      videoRef.current?.pause();
    }
  }

  return (
    <video
      ref={videoRef}
      preload="true"
      loop={false}
      onPlay={() => applyImageSource()}
      onTimeUpdate={() => pauseVideo()}
      className={classNames(classes, homeCSS.videoCss)}
    >
      <source src={videoSrc} type="video/mp4" />
    </video>
  );
};

const RoomImage = ({ imageSrc, classes }: { imageSrc: string; classes: string }) => {
  return <img className={classNames(homeCSS.imageCSS, classes)} alt="slika sobe" src={imageSrc}></img>;
};
